import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import Lenis from 'lenis';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { CountryProvider } from '@/context/CountryContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import CountryRouteWrapper from '@/components/CountryRouteWrapper';
import SEOHead from '@/components/SEOHead';
import MobileBottomNav from '@/components/MobileBottomNav';
import ScrollToTop from '@/components/ScrollToTop';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import OnboardingPage from '@/pages/OnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import PropertiesPage from '@/pages/PropertiesPage';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import ConciergePage from '@/pages/ConciergePage';
import MarketplacePage from '@/pages/MarketplacePage';
import MarketplaceDetailPage from '@/pages/MarketplaceDetailPage';
import WishlistPage from '@/pages/WishlistPage';
import PortfolioPreviewPage from '@/pages/PortfolioPreviewPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import InvestorModePage from '@/pages/InvestorModePage';
import PortfolioInsightsDashboard from '@/pages/PortfolioInsightsDashboard';
import DeveloperPanelPage from '@/pages/DeveloperPanelPage';
import AgentCRMPage from '@/pages/AgentCRMPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import AdminPanelPage from '@/pages/AdminPanelPage';
import AdminMigrationsPage from '@/pages/AdminMigrationsPage';
import SettingsPage from '@/pages/SettingsPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
// Partner Pages
import PartnerLandingPage from '@/pages/PartnerLandingPage';
import IndiaPartnerLandingPage from '@/pages/IndiaPartnerLandingPage';
import PartnerLoginPage from '@/pages/PartnerLoginPage';
import PartnerSetupPage from '@/pages/PartnerSetupPage';
import DeveloperDashboard from '@/pages/partner/DeveloperDashboard';
import BrokerAgentDashboard from '@/pages/partner/BrokerAgentDashboard';
import CarDealerDashboard from '@/pages/partner/CarDealerDashboard';
import GenericPartnerDashboard from '@/pages/partner/GenericPartnerDashboard';
import AccountPage from '@/pages/AccountPage';
import WorkflowPage from '@/pages/WorkflowPage';
import PartnerAccountPage from '@/pages/partner/PartnerAccountPage';
import PartnerListingPage from '@/pages/PartnerListingPage';
import PartnerListingsIndexPage from '@/pages/PartnerListingsIndexPage';
import LegalPage from '@/pages/LegalPage';
import '@/App.css';

// Smooth scrolling hook using Lenis
const useSmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      // Prevent smooth scroll on elements with data-lenis-prevent
      prevent: (node) => node.hasAttribute('data-lenis-prevent') || 
                         node.closest('[data-lenis-prevent]') !== null,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Store lenis instance for potential pause/resume
    window.lenis = lenis;

    return () => {
      lenis.destroy();
      delete window.lenis;
    };
  }, []);
};

const PARTNER_ROLES = ['developer', 'broker', 'agent', 'car_dealer', 'yacht_dealer', 'interior_designer', 'other_business'];

const PARTNER_ROLE_DASHBOARDS = {
  developer: '/partner/dashboard/developer',
  broker: '/partner/dashboard/broker',
  agent: '/partner/dashboard/agent',
  car_dealer: '/partner/dashboard/cars',
  yacht_dealer: '/partner/dashboard/yachts',
  interior_designer: '/partner/dashboard/design',
  other_business: '/partner/dashboard/services',
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          <div className="text-gold text-lg font-playfair animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  // Cross-portal block: a partner role MUST NOT see customer routes.
  if (PARTNER_ROLES.includes(user.role)) {
    return <Navigate to={PARTNER_ROLE_DASHBOARDS[user.role] || '/partner/dashboard'} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading...</div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" />;
};

const ADMIN_EMAILS = ['admin@dreamlife.com', 'admin@thedreamlife.app', 'admin@dreamlife.app'];

const isAdminUser = (user) => {
  if (!user) return false;
  return (
    user.role === 'admin' ||
    user.role === 'super_admin' ||
    ADMIN_EMAILS.includes((user.email || '').toLowerCase())
  );
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (!isAdminUser(user)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const PartnerRoute = ({ children }) => {
  // Partner auth uses localStorage (separate session from main cookie-based auth)
  const { getAuthToken, getStoredUser } = require('@/utils/auth');
  const token = getAuthToken();
  const user = getStoredUser();
  
  if (!token || !user) {
    return <Navigate to="/partner/login" />;
  }

  const partnerRoles = ['developer', 'broker', 'agent', 'car_dealer', 'yacht_dealer', 'interior_designer', 'other_business'];

  // Admins/super_admins can access partner routes for management/QA purposes
  if (!partnerRoles.includes(user.role) && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/partner/login" />;
  }
  return children;
};

function CountryAppRoutes() {
  return (
    <Routes>
      <Route path="login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="marketplace/:id" element={<ProtectedRoute><MarketplaceDetailPage /></ProtectedRoute>} />
      <Route path="wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="portfolio" element={<ProtectedRoute><PortfolioPreviewPage /></ProtectedRoute>} />
      <Route path="concierge" element={<ProtectedRoute><ConciergePage /></ProtectedRoute>} />
      <Route path="subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      <Route path="investor" element={<ProtectedRoute><InvestorModePage /></ProtectedRoute>} />
      <Route path="investor/insights" element={<ProtectedRoute><PortfolioInsightsDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  // Enable luxury smooth scrolling
  useSmoothScroll();
  
  return (
    <Routes>
      {/* SEO Head */}
      
      {/* Public Routes (backward-compatible, no country prefix) */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      
      {/* Protected Customer Routes */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/marketplace/:id" element={<ProtectedRoute><MarketplaceDetailPage /></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioPreviewPage /></ProtectedRoute>} />
      <Route path="/concierge" element={<ProtectedRoute><ConciergePage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/investor" element={<ProtectedRoute><InvestorModePage /></ProtectedRoute>} />
      <Route path="/investor/insights" element={<ProtectedRoute><PortfolioInsightsDashboard /></ProtectedRoute>} />
      <Route path="/developer" element={<ProtectedRoute><DeveloperPanelPage /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute><AgentCRMPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      
      <Route path="/workflow" element={<WorkflowPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminPanelPage /></AdminRoute>} />
      <Route path="/admin/migrations" element={<AdminRoute><AdminMigrationsPage /></AdminRoute>} />
      
      {/* Partner Portal Routes (Public) */}
      <Route path="/partner" element={<PartnerLandingPage />} />
      <Route path="/partner/apply" element={<PartnerLandingPage />} />
      <Route path="/partner/login" element={<PartnerLoginPage />} />
      <Route path="/partner/setup" element={<PartnerSetupPage />} />

      {/* Legal / T&C / Privacy / Partner Agreement (single source of truth) */}
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />

      {/* India Partner Portal (region-specific public pages) */}
      <Route path="/in/partner" element={<IndiaPartnerLandingPage />} />
      <Route path="/in/partner/apply" element={<IndiaPartnerLandingPage />} />
      <Route path="/in/partner/login" element={<PartnerLoginPage />} />
      <Route path="/in/partner/setup" element={<PartnerSetupPage />} />
      {/* India Partner — preferred long-form aliases (per product spec) */}
      <Route path="/india/partner" element={<IndiaPartnerLandingPage />} />
      <Route path="/india/partner/apply" element={<IndiaPartnerLandingPage />} />
      <Route path="/india/partner/login" element={<PartnerLoginPage />} />
      <Route path="/india/partner/setup" element={<PartnerSetupPage />} />

      {/* UAE Partner Portal — explicit alias to the existing premium-positioned page */}
      <Route path="/uae/partner" element={<PartnerLandingPage />} />
      <Route path="/uae/partner/apply" element={<PartnerLandingPage />} />
      <Route path="/uae/partner/login" element={<PartnerLoginPage />} />
      <Route path="/uae/partner/setup" element={<PartnerSetupPage />} />
      
      {/* Partner Dashboard Routes (Protected) */}
      <Route path="/partner/account" element={<PartnerRoute><PartnerAccountPage /></PartnerRoute>} />
      <Route path="/partner/listings/new" element={<PartnerRoute><PartnerListingPage /></PartnerRoute>} />
      <Route path="/partner/listings/new/:assetType" element={<PartnerRoute><PartnerListingPage /></PartnerRoute>} />
      <Route path="/partner/listings/:assetType" element={<PartnerRoute><PartnerListingsIndexPage /></PartnerRoute>} />
      <Route path="/partner/listings/:assetType/:listingId" element={<PartnerRoute><PartnerListingPage /></PartnerRoute>} />
      <Route path="/partner/dashboard" element={<PartnerRoute><GenericPartnerDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/developer" element={<PartnerRoute><DeveloperDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/broker" element={<PartnerRoute><BrokerAgentDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/agent" element={<PartnerRoute><BrokerAgentDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/cars" element={<PartnerRoute><CarDealerDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/yachts" element={<PartnerRoute><GenericPartnerDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/design" element={<PartnerRoute><GenericPartnerDashboard /></PartnerRoute>} />
      <Route path="/partner/dashboard/services" element={<PartnerRoute><GenericPartnerDashboard /></PartnerRoute>} />

      {/* Country-prefixed SEO routes (dynamic — matched after all static routes) */}
      <Route path="/:country" element={<CountryRouteWrapper><LandingPage /></CountryRouteWrapper>} />
      <Route path="/:country/*" element={<CountryRouteWrapper><CountryAppRoutes /></CountryRouteWrapper>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CurrencyProvider>
          <CountryProvider>
            <BrowserRouter>
              <ScrollToTop />
              <SEOHead />
              <AppRoutes />
              <MobileBottomNav />
              <Toaster position="top-right" />
            </BrowserRouter>
          </CountryProvider>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
